import { isTransientApiError } from "../api/fetch";

export interface UserLike {
	id?: number | string | null;
	email?: string | null;
	username?: string | null;
}

/** The part of a Sentry event this module reads: the mechanism the SDK stamps
 * on an exception it picked up itself (`handled: false`) rather than one a
 * `captureException` call handed it. */
interface EventWithMechanism {
	exception?: { values?: Array<{ mechanism?: { handled?: boolean } }> };
}

/** A gateway failure nobody caught, which is what a prod deploy window looks
 * like from the browser: the containers are gone for 30-90 s, every open page
 * polls into it, and each uncaught rejection opens a Sentry event for an
 * outage we already accept.
 *
 * Both halves are required. A 403 or a 500 is a real failure whatever caught
 * it, and a transient status reported deliberately through `captureException`
 * is somebody asking to see it. */
export function isUnhandledTransientApiError(
	event: EventWithMechanism,
	hint: { originalException?: unknown }
): boolean {
	if (!isTransientApiError(hint.originalException)) {
		return false;
	}
	const values = event.exception?.values ?? [];
	return values.some((value) => value.mechanism?.handled === false);
}

/** Report a caught error to Sentry, if Sentry is running in this build.
 *
 * Sentry is lazy-loaded and only initialised where a DSN is configured (see
 * frontend/index.tsx), so every caller goes through this rather than reaching
 * for the SDK itself. */
export function reportErrorToSentry(error: Error, componentStack?: string): void {
	void import("@sentry/react")
		.then((Sentry) => {
			if (Sentry.isInitialized()) {
				Sentry.captureException(error, {
					extra: { componentStack },
				});
			}
		})
		.catch((err) => {
			console.error("Failed to load Sentry for error reporting", err);
		});
}

export function parseJwtUserId(token: string): string | null {
	try {
		const parts = token.split(".");
		if (parts.length < 2) return null;
		const base64Url = parts[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
				.join("")
		);
		const payload = JSON.parse(jsonPayload) as { user_id?: number | string };
		if (typeof payload.user_id === "number") return String(payload.user_id);
		if (typeof payload.user_id === "string") return payload.user_id;
		return null;
	} catch (_e) {
		return null;
	}
}

export function setClientContext(): void {
	void import("@sentry/react")
		.then((Sentry) => {
			if (!Sentry.isInitialized()) return;
			const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const utcOffsetMinutes = -new Date().getTimezoneOffset();
			const locale = navigator.language;
			const now = new Date();
			Sentry.setContext("client", {
				timeZone,
				locale,
				localTime: now.toString(),
				localISOString: now.toISOString(),
				utcOffsetMinutes,
				userAgent: navigator.userAgent,
			});
		})
		.catch(() => {
			// Sentry not available, ignore
		});
}

export function syncSentryAuthState(
	currentUser?: UserLike,
	users?: Array<{
		id: number | string;
		username?: string | null;
		email?: string | null;
	}>
): void {
	void import("@sentry/react")
		.then((Sentry) => {
			if (!Sentry.isInitialized()) {
				return;
			}

			const accessToken = localStorage.getItem("access_token");
			const originalAccessToken = localStorage.getItem("original_access_token");
			const isImpersonating = Boolean(originalAccessToken);

			const impersonatedUserId = accessToken ? parseJwtUserId(accessToken) : null;
			const realUserId = originalAccessToken
				? parseJwtUserId(originalAccessToken)
				: null;

			const resolveUser = (targetId: string | null): UserLike | undefined => {
				if (!targetId) {
					return undefined;
				}
				if (currentUser && String(currentUser.id) === targetId) {
					return currentUser;
				}
				return users?.find((candidate) => String(candidate.id) === targetId);
			};

			const impersonatedUser = resolveUser(impersonatedUserId);
			const realUser = resolveUser(realUserId);

			const displayUserId = isImpersonating ? realUserId : impersonatedUserId;
			const displayUser = isImpersonating
				? (realUser ?? currentUser)
				: (impersonatedUser ?? currentUser);
			const fallbackId =
				displayUserId ??
				(displayUser?.id != null ? String(displayUser.id) : null);

			if (!fallbackId) {
				Sentry.setUser(null);
				return;
			}

			const payload: Record<string, unknown> = {
				id: fallbackId,
				"User ID": fallbackId,
				Impersonating: isImpersonating ? "True" : "False",
			};

			const username = displayUser?.username ?? currentUser?.username;
			if (username) {
				payload.Username = username;
			}

			const email = displayUser?.email ?? currentUser?.email;
			if (email) {
				payload.email = email;
			}

			if (isImpersonating) {
				payload["Impersonated user id"] = impersonatedUserId ?? "(unknown)";
				payload["Impersonated username"] =
					impersonatedUser?.username ?? "(unknown)";
			}

			Sentry.setUser(payload as any);
		})
		.catch(() => {
			// Sentry not available, ignore
		});
}
